import omit from 'lodash/omit'

import client, { Schemas } from '~/clients/api-client'
import { useStore } from '~/store/store'
import { type HospitalConfigPractitioner, selectDepartmentBySpecialityId } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'

import type { ClientResponse } from '../../Form/formErrors'
import { getListDifferencesBy, hasDifference } from '../../Form/utils'
import type { PractitionerFormDefaultValues, PractitionerFormParsedValues, ProfileWithSurgeryTypeGroups } from './formState'
import { formStateToApiPayloads } from './formState'

const hasSaveError = (results: ClientResponse[]) => results.some(r => Boolean(r.error))

/**
 * Creates a practitioner profile with related surgery type groups
 * @param profiles
 * @returns
 */
async function createBatchPractitionerProfiles(profiles: ProfileWithSurgeryTypeGroups[]): Promise<ClientResponse> {
    const profileToSurgeryTypeGroups = new Map<string, ProfileWithSurgeryTypeGroups['profileSurgeryTypeGroups']>()
    const profilesWithUuid = profiles.map(p => {
        const uuid = crypto.randomUUID()
        profileToSurgeryTypeGroups.set(uuid, p.profileSurgeryTypeGroups)
        return {
            active_from: p.active_from,
            practitioner_id: p.practitioner_id,
            extra_staffing_config: p.extra_staffing_config,
            uuid,
        }
    })

    const res = await client.createBatchPractitionerProfile(profilesWithUuid)
    const createdProfiles = res.data?.filter(Boolean)

    if (!createdProfiles || createdProfiles.length !== profilesWithUuid.length) {
        console.error('Failed to create profiles', { cause: res.error })
        return res
    }
    // relation uses Id, so we need to get the id from the newly created profile
    const surgeryTypeGroupsToCreate = createdProfiles.flatMap(p => {
        const surgeryTypeGroups = profileToSurgeryTypeGroups.get(p.uuid)
        if (!surgeryTypeGroups) return []
        const surgeryTypeGroupsWithId = surgeryTypeGroups.map(s => ({
            practitioner_profile_id: p.id,
            surgery_type_group_id: s.surgery_type_group_id,
            status: s.status,
        }))
        return surgeryTypeGroupsWithId
    })
    return client.createBatchPractitionerProfileSurgeryTypeGroup(surgeryTypeGroupsToCreate)
}

export async function saveEditPractitioner(
    values: PractitionerFormParsedValues,
    initialValues: PractitionerFormDefaultValues,
    practitioner: HospitalConfigPractitioner
): Promise<ClientResponse[]> {
    const payloads = formStateToApiPayloads(values, practitioner.id)
    // also transform "initialPayloads" to have a normalised structure to compare
    const initialPayloads = formStateToApiPayloads(initialValues, practitioner.id)

    const promises: Promise<ClientResponse>[] = []

    // Practitioner
    if (values.specialityId !== initialValues.specialityId) {
        promises.push(client.partialUpdatePractitioner(practitioner.id, payloads.practitioner))

        // handle legacy department assignments
        // keep in sync with speciality for now
        const department = selectDepartmentBySpecialityId(useStore.getState(), values.specialityId)
        const assignmentsToDelete = practitioner.departmentAssignments.filter(d => d.department_id !== department?.id)
        const hasDepartmentAssignment =
            practitioner.departmentAssignments.length > 0 && assignmentsToDelete.length !== practitioner.departmentAssignments.length

        if (assignmentsToDelete.length > 0) {
            promises.push(client.deleteBatchDepartmentPractitionerAssignments(assignmentsToDelete.map(d => d.id)))
        }
        if (department && !hasDepartmentAssignment) {
            promises.push(
                client.createDepartmentPractitionerAssignment({
                    practitioner_id: practitioner.id,
                    department_id: department.id,
                })
            )
        }
    }

    // Practitioner profiles: create, update, delete

    const profilesDiff = getListDifferencesBy(payloads.practitionerProfiles, initialPayloads.practitionerProfiles, p => p.id)

    if (profilesDiff.added.length > 0) {
        promises.push(createBatchPractitionerProfiles(profilesDiff.added))
    }
    if (profilesDiff.removed.length > 0) {
        promises.push(client.deleteBatchPractitionerProfiles(profilesDiff.removed.map(p => p.id).filter(Boolean)))
    }

    const batchUpdates = {
        practitionerProfileUpdates: [] as Schemas['BaseEntitySchemaUpdate_PractitionerProfileCreate_'][],
        practitionerProfileSurgeryTypeGroupCreates: [] as Schemas['PractitionerProfileSurgeryTypeGroupCreate'][],
        practitionerProfileSurgeryTypeGroupUpdates: [] as Schemas['BaseEntitySchemaUpdate_PractitionerProfileSurgeryTypeGroupCreate_'][],
        practitionerProfileSurgeryTypeGroupDeletes: [] as number[],
    }
    profilesDiff.updated.forEach(({ new: newProfile, initial: initialProfile }) => {
        const initialProfileId = initialProfile.id
        if (!initialProfileId) return

        const primitiveInitialProfile = omit(initialProfile, 'profileSurgeryTypeGroups')
        const primitiveNewProfile = omit(newProfile, 'profileSurgeryTypeGroups')
        const hasProfileDiff = hasDifference(primitiveNewProfile, primitiveInitialProfile)
        if (hasProfileDiff) {
            batchUpdates.practitionerProfileUpdates.push({ id: initialProfileId, body: primitiveNewProfile })
        }

        const initialGroups = initialProfile.profileSurgeryTypeGroups.map(s => ({ ...s, practitioner_profile_id: initialProfileId }))
        const newGroups = newProfile.profileSurgeryTypeGroups.map(s => ({ ...s, practitioner_profile_id: initialProfileId }))

        // we want distinct surgeryTypeGroups per profile, as there's a unique constrain across practitioner_profile_id and surgery_type_group_id
        // and can thus get differences by s.surgert_type_group_id only (since we're scoped to a profile).
        const profileSurgeryTypeGroupDiffs = getListDifferencesBy(newGroups, initialGroups, s => s.surgery_type_group_id)
        if (profileSurgeryTypeGroupDiffs.added.length > 0) {
            batchUpdates.practitionerProfileSurgeryTypeGroupCreates.push(...profileSurgeryTypeGroupDiffs.added)
        }

        if (profileSurgeryTypeGroupDiffs.removed.length > 0) {
            batchUpdates.practitionerProfileSurgeryTypeGroupDeletes.push(...profileSurgeryTypeGroupDiffs.removed.map(s => s.id).filter(Boolean))
        }
        if (profileSurgeryTypeGroupDiffs.updated.length > 0) {
            batchUpdates.practitionerProfileSurgeryTypeGroupUpdates.push(
                ...profileSurgeryTypeGroupDiffs.updated.map(s => ({
                    id: s.initial.id,
                    body: { ...s.new, practitioner_profile_id: initialProfileId },
                }))
            )
        }
    })

    // profile updates
    if (batchUpdates.practitionerProfileUpdates.length > 0) {
        promises.push(client.updateBatchPractitionerProfiles(batchUpdates.practitionerProfileUpdates))
    }
    if (batchUpdates.practitionerProfileSurgeryTypeGroupCreates.length > 0) {
        promises.push(client.createBatchPractitionerProfileSurgeryTypeGroup(batchUpdates.practitionerProfileSurgeryTypeGroupCreates))
    }
    if (batchUpdates.practitionerProfileSurgeryTypeGroupDeletes.length > 0) {
        promises.push(client.deleteBatchPractitionerProfileSurgeryTypeGroups(batchUpdates.practitionerProfileSurgeryTypeGroupDeletes))
    }
    if (batchUpdates.practitionerProfileSurgeryTypeGroupUpdates.length > 0) {
        promises.push(client.updateBatchPractitionerProfileSurgeryTypeGroups(batchUpdates.practitionerProfileSurgeryTypeGroupUpdates))
    }

    // Service periods
    const servicePeriodsDiff = getListDifferencesBy(payloads.servicePeriods, initialPayloads.servicePeriods, p => p.id)
    const newServicePeriods = servicePeriodsDiff.added
    if (newServicePeriods.length > 0) {
        promises.push(client.createBatchPractitionerServicePeriod(newServicePeriods))
    }
    const updatedServicePeriods = servicePeriodsDiff.updated.map(s => ({ body: s.new, id: s.initial.id }))
    if (updatedServicePeriods.length > 0) {
        promises.push(client.updateBatchPractitionerServicePeriods(updatedServicePeriods))
    }
    const deletedServicePeriods = servicePeriodsDiff.removed.map(s => s.id).filter(Boolean)
    if (deletedServicePeriods.length > 0) {
        promises.push(client.deleteBatchPractitionerServicePeriods(deletedServicePeriods))
    }

    const results = await Promise.all(promises)

    if (!hasSaveError(results)) {
        void trackUserEvent('HOSPITAL_CONFIGURATION_PRACTITIONER_EDITED', {
            profileCount: payloads.practitionerProfiles.length,
            servicePeriodCount: payloads.servicePeriods.length,
            hasChanges: promises.length > 0,
        })
    }
    return results
}

export const saveNewPractitioner = async (values: PractitionerFormParsedValues, dipsResourceId: number | undefined): Promise<ClientResponse[]> => {
    const promises: Promise<ClientResponse>[] = []
    const practitionerCreationPromise = client.createPractitioner({
        role: 'surgeon',
        short_name: values.shortName,
        speciality_id: values.specialityId,
        dips_id: dipsResourceId,
    })
    promises.push(practitionerCreationPromise)
    const practitionerResponse = await practitionerCreationPromise
    if (practitionerResponse.error) {
        return Promise.resolve([practitionerResponse])
    }
    const practitionerId = practitionerResponse.data.id
    const payloads = formStateToApiPayloads(values, practitionerId)

    if (payloads.servicePeriods.length > 0) {
        promises.push(client.createBatchPractitionerServicePeriod(payloads.servicePeriods))
    }
    if (payloads.practitionerProfiles.length > 0) {
        promises.push(createBatchPractitionerProfiles(payloads.practitionerProfiles))
    }

    const department = selectDepartmentBySpecialityId(useStore.getState(), values.specialityId)
    if (department) {
        promises.push(
            client.createDepartmentPractitionerAssignment({
                practitioner_id: practitionerId,
                department_id: department.id,
            })
        )
    }

    const results = await Promise.all(promises)
    if (!hasSaveError(results)) {
        void trackUserEvent('HOSPITAL_CONFIGURATION_PRACTITIONER_CREATED', {
            profileCount: payloads.practitionerProfiles.length,
            servicePeriodCount: payloads.servicePeriods.length,
        })
    }
    return results
}
